import { useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/authService';

export const useAuth = () => {
  const { 
    user,
    sessionToken, 
    isAuthenticated, 
    isLoading,
    error,
    setUser,
    setSessionToken, 
    clearAuth,
    setLoading,
    setError,
  } = useAuthStore();

  const loginAsGuest = useCallback(async (dobClaim: Date) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.createGuest(dobClaim);
      setSessionToken(response.sessionToken);
      setUser({
        id: response.userId,
        isGuest: true,
      });
    } catch (err) {
      setError('Failed to create guest account');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setSessionToken, setUser]);

  const setDobClaim = useCallback(async (dobClaim: Date) => {
    try {
      await authService.setDobClaim(dobClaim);
    } catch (err) {
      // Non-fatal — log and continue
      console.error('Failed to store DOB claim:', err);
    }
  }, []);

  const sendRecoveryLink = useCallback(async (email: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await authService.sendRecoveryLink(email);
      return res;
    } catch (err) {
      setError('Failed to send recovery link');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError]);

  const verifyAccountRecovery = useCallback(async (token: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await authService.verifyRecoveryToken(token);
      setSessionToken(res.sessionToken);
      // After setting token, fetch user details
      const userRes = await authService.getCurrentUser();
      if (userRes.user) {
        setUser(userRes.user);
      }
      return res;
    } catch (err) {
      setError('Failed to verify recovery token');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setSessionToken, setUser]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (err) {
      // Ignore errors, clear local state anyway
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response.user) {
        setUser(response.user);
      } else {
        clearAuth();
      }
    } catch (err) {
      clearAuth();
    }
  }, [setUser, clearAuth]);

  return {
    user,
    sessionToken,
    isAuthenticated,
    isLoading,
    error,
    loginAsGuest,
    setDobClaim,
    sendRecoveryLink,
    verifyAccountRecovery,
    logout,
    refreshUser: refreshUser,
  };
};
